// ui/useGameLoop.js: Preact hook that pairs gameReducer with a
// LuaGameState instance (M2.c-10b).
//
// Two effects:
//   1. On mount (or LuaGameState change), pull the initial snapshot
//      into reducer state via SET_SNAPSHOT.
//   2. Whenever state.pendingCommand transitions non-null, run the
//      corresponding LuaGameState method, then dispatch APPLIED with
//      the fresh snapshot (or FAILED on throw).
//
// The hook itself contains no game-rule logic, that all lives in Lua.
// Bot scheduling (M2.c-10c), lifecycle (M2.c-10d), and game-over UI
// (M2.c-10e) layer on top of this hook by observing the reducer state
// and dispatching the appropriate command actions.

import { useReducer, useEffect } from 'preact/hooks';
import { gameReducer, INITIAL_STATE, ACTIONS, LIFECYCLE, actionCreators } from './gameReducer.js';

/**
 * Drives a LuaGameState instance via a useReducer-backed command
 * queue. Returns [state, dispatch, helpers].
 *
 * @param {import('../lua/game/lua_bridge.js').LuaGameState|null} luaGameState
 * @returns {[
 *   ReturnType<typeof gameReducer>,
 *   (action: any) => void,
 *   typeof actionCreators
 * ]}
 */
export function useGameLoop(luaGameState) {
  const [state, dispatch] = useReducer(gameReducer, INITIAL_STATE);

  // Effect #1: initial snapshot. Runs once per luaGameState instance.
  useEffect(() => {
    if (!luaGameState) return;
    try {
      const snap = luaGameState.snapshot(1);
      dispatch(actionCreators.setSnapshot(snap));
    } catch (err) {
      dispatch(actionCreators.commandFailed(errMsg(err)));
    }
  }, [luaGameState]);

  // Effect #2: drain the command queue.
  useEffect(() => {
    if (!luaGameState || !state.pendingCommand) return;
    const cmd = state.pendingCommand;
    let result = null;
    try {
      switch (cmd.type) {
        case ACTIONS.DEAL:
          luaGameState.deal(cmd.seed);
          break;
        case ACTIONS.SUBMIT_BID:
          result = luaGameState.submitBid(cmd.seat, cmd.bid);
          break;
        case ACTIONS.DEALER_DISCARD:
          result = luaGameState.dealerDiscard(cmd.cardId);
          break;
        case ACTIONS.PLAY_CARD:
          result = luaGameState.playCard(cmd.seat, cmd.cardId);
          break;
        case ACTIONS.SCORE:
          result = luaGameState.scoreHand();
          break;
        case ACTIONS.RESET_GAME:
          luaGameState.reset(cmd.settings || {});
          break;
        case ACTIONS.NEXT_HAND:
          luaGameState.nextHand(cmd.nextDealerSeat, cmd.nextSeed);
          break;
        default:
          dispatch(actionCreators.commandFailed(`unknown command: ${cmd.type}`));
          return;
      }
      const snap = luaGameState.snapshot(1);
      dispatch(actionCreators.commandApplied(snap, result));
    } catch (err) {
      dispatch(actionCreators.commandFailed(errMsg(err)));
    }
  }, [luaGameState, state.pendingCommand]);

  return [state, dispatch, actionCreators];
}

function errMsg(err) {
  if (err instanceof Error) return err.message;
  return String(err);
}

// Re-export so callers don't have to import gameReducer + useGameLoop
// separately.
export { ACTIONS, LIFECYCLE, INITIAL_STATE, actionCreators };
